import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * A small `(?)` help affordance next to a field label. The extended text shows
 * on hover, on keyboard focus, and on click (touch/persist). Accessible: the
 * trigger is a real button labelled for screen readers and `aria-describedby`
 * points at the tooltip bubble (`role="tooltip"`), which stays in the DOM.
 *
 * See `docs/ui-styleguide.md` — this is the standard field-help pattern.
 */
export function InfoTooltip({ text }: { text: string }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span className="info-tip">
      <button
        type="button"
        className="info-tip__trigger"
        aria-label={t("common.fieldHelp")}
        aria-expanded={open}
        aria-describedby={id}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
      >
        ?
      </button>
      <span
        id={id}
        role="tooltip"
        className={open ? "info-tip__bubble info-tip__bubble--open" : "info-tip__bubble"}
      >
        {text}
      </span>
    </span>
  );
}
